import { shallowReactive } from 'vue';

/// Type

/**
 * Semantic category that drives both the toast's visual treatment (icon + color) and its
 * `aria-live` politeness — `error` is announced `assertive`; the rest `polite`.
 */
export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

/**
 * Caller-tunable knobs accepted by every variant shorthand and by `show()`.
 */
export interface ToastOptions {
  /**
   * Optional bold heading rendered above the message body. Use when the variant + message alone
   * don't carry enough context (e.g., a single-word "Saved" doesn't need one).
   */
  title?: string;
  /**
   * Auto-dismiss timeout in milliseconds. Defaults to 5000. Ignored when `sticky` is true.
   */
  duration?: number;
  /**
   * Suppresses auto-dismiss entirely — the toast stays until the user clicks close or swipes it
   * away. Typical use: error toasts the user must acknowledge.
   */
  sticky?: boolean;
}

/**
 * Materialised toast as it lives in the store and is rendered by `AppToaster`.
 */
export interface Toast extends ToastOptions {
  id: number;
  message: string;
  variant: ToastVariant;
}

/// Data

/**
 * Reactive store consumed by `AppToaster`. Module-level so any component can push without needing
 * to wire props or events.
 */
export const toasts = shallowReactive<Toast[]>([]);

let nextId = 1;

/// Methods

/**
 * Push a toast onto the store. Returns the monotonic id so the caller can dismiss programmatically
 * (e.g., a long-running task that fires a sticky "Saving…" and then dismisses it on completion).
 *
 * @param toast - Variant + message plus optional title / duration / sticky overrides.
 * @returns The new toast's id.
 */
export function show(toast: Omit<Toast, 'id'>): number {
  const id = nextId++;
  toasts.push({ ...toast, id });
  return id;
}

/**
 * Remove a single toast by id. No-op if the id isn't in the store (e.g., already auto-dismissed).
 */
export function dismiss(id: number): void {
  const index = toasts.findIndex((t) => t.id === id);
  if (index !== -1) toasts.splice(index, 1);
}

/**
 * Clear every toast immediately. Useful on route changes or after a destructive action that
 * supersedes prior notifications.
 */
export function dismissAll(): void {
  toasts.length = 0;
}

/// Composed

/**
 * Imperative toast API. Returns variant shorthands plus `dismiss` / `dismissAll` so the caller can
 * fire-and-forget or hold the id and clear later.
 *
 * @example
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error('Save failed', { sticky: true });
 *   const id = toast.info('Refreshing…');
 *   // ...later
 *   toast.dismiss(id);
 */
export function useToast() {
  return {
    info: (message: string, options?: ToastOptions) =>
      show({ message, variant: 'info', ...options }),
    success: (message: string, options?: ToastOptions) =>
      show({ message, variant: 'success', ...options }),
    warning: (message: string, options?: ToastOptions) =>
      show({ message, variant: 'warning', ...options }),
    error: (message: string, options?: ToastOptions) =>
      show({ message, variant: 'error', ...options }),
    dismiss,
    dismissAll,
  };
}
